import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useAdminLocale } from '../../lib/admin-locale.js';
import { Icon } from './Icon.js';
import { cx } from './utils.js';

export type DatePreset =
  'all' | 'today' | 'last-week' | 'last-month' | 'last-3-months' | 'current-month' | 'current-year';

const OPTIONS: Array<{ value: DatePreset; label: readonly [string, string] }> = [
  { value: 'all', label: ['Όλες οι ημερομηνίες', 'All dates'] },
  { value: 'today', label: ['Σήμερα', 'Today'] },
  { value: 'last-week', label: ['Τελευταίες 7 ημέρες', 'Last 7 days'] },
  { value: 'last-month', label: ['Τελευταίες 30 ημέρες', 'Last 30 days'] },
  { value: 'last-3-months', label: ['Τελευταίοι 3 μήνες', 'Last 3 months'] },
  { value: 'current-month', label: ['Τρέχων μήνας', 'Current month'] },
  { value: 'current-year', label: ['Τρέχον έτος', 'Current year'] },
];

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function ymd(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Inclusive local range for a preset. `all` has no bounds. */
export function datePresetRange(
  preset: DatePreset,
  now = new Date(),
): { from: string; to: string } | null {
  if (preset === 'all') return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'today') return { from: ymd(today), to: ymd(today) };
  if (preset === 'last-week') {
    const from = new Date(today);
    from.setDate(today.getDate() - 6);
    return { from: ymd(from), to: ymd(today) };
  }
  if (preset === 'last-month') {
    const from = new Date(today);
    from.setDate(today.getDate() - 29);
    return { from: ymd(from), to: ymd(today) };
  }
  if (preset === 'last-3-months') {
    const from = new Date(today);
    from.setDate(today.getDate() - 89);
    return { from: ymd(from), to: ymd(today) };
  }
  if (preset === 'current-month') {
    return {
      from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: ymd(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    };
  }
  return {
    from: ymd(new Date(today.getFullYear(), 0, 1)),
    to: ymd(new Date(today.getFullYear(), 11, 31)),
  };
}

function preview(preset: DatePreset): string | null {
  const range = datePresetRange(preset);
  if (!range) return null;
  const format = (value: string) => {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  };
  return range.from === range.to
    ? format(range.from)
    : `${format(range.from)} – ${format(range.to)}`;
}

export type DateSelectProps = {
  value: DatePreset;
  onChange: (value: DatePreset) => void;
  className?: string;
  'aria-label'?: string;
};

export function DateSelect({
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: DateSelectProps): ReactNode {
  const { text } = useAdminLocale();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = OPTIONS.find((option) => option.value === value) ?? OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cx('relative min-w-0', className)} data-ui="date-select">
      <button
        type="button"
        aria-label={ariaLabel ?? text('Περίοδος', 'Period')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((currentOpen) => !currentOpen)}
        className="flex h-10 w-full items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-left text-sm shadow-sm hover:border-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-950 dark:hover:border-slate-500"
      >
        <Icon name="calendar" className="size-4 shrink-0 text-slate-400" />
        <span className="min-w-0 flex-1 truncate text-slate-900 dark:text-slate-100">
          {current ? text(...current.label) : ''}
        </span>
        <Icon
          name="chevron"
          className={cx('size-4 shrink-0 text-slate-400', open && 'rotate-180')}
        />
      </button>
      {open ? (
        <div
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-900"
        >
          {OPTIONS.map((option) => {
            const active = option.value === value;
            const range = preview(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                className={cx(
                  'flex w-full flex-col px-3 py-2 text-left text-sm',
                  active
                    ? 'bg-[color:var(--admin-selection-bg,#2563EB)] text-[color:var(--admin-selection-text,#FFFFFF)]'
                    : 'text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800',
                )}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span>{text(...option.label)}</span>
                {range ? (
                  <span className={cx('text-xs', active ? 'text-white/80' : 'text-slate-500')}>
                    {range}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
